



import { useEffect, useState } from "react";
import {
  getFeedsApi,
  deleteFeedApi,
  likeFeedApi,
  unlikeFeedApi,
  addCommentApi,
} from "../../app/api";
import { 
  Heart, 
  MessageCircle, 
  Share2, 
  MoreHorizontal, 
  Send, 
  Trash2,
  Calendar,
  Sparkles,
  Trophy,
  Star
} from "lucide-react";
import CreatePostModal from "./CreatePostModal";
import { toast } from "react-hot-toast";
import moment from "moment";
import { motion } from "framer-motion";

const IMAGE_BASE_URL = "https://uptask.visiontrek.io";
 
export default function Feeds({ showModal, setShowModal }) {
 
  const [feeds, setFeeds] = useState([]);
  const [savedPosts, setSavedPosts] = useState([]);
 
  useEffect(() => {
    loadFeeds();
  }, []);
 
  const loadFeeds = async () => {
    const res = await getFeedsApi();
    if (res.success) setFeeds(res.data);
  };
 
  const handleDelete = async (id) => {
    const res = await deleteFeedApi(id);
    if (res.success) loadFeeds();
  };
 
  const handleLike = async (feed) => {
    // Optimistic update
    const updatedFeeds = feeds.map(f => {
      if (f.id === feed.id) {
        return {
          ...f,
          is_liked: !f.is_liked,
          total_likes: f.is_liked ? (f.total_likes - 1) : (f.total_likes + 1)
        };
      }
      return f;
    });
    setFeeds(updatedFeeds);

    const res = feed.is_liked ? await unlikeFeedApi(feed.id) : await likeFeedApi(feed.id);
    if (!res.success) {
      // Rollback if failed
      loadFeeds();
      toast.error("Failed to update like");
    }
  };
 
  const handleComment = async (id, comment) => {
    if (!comment.trim()) return;
    const res = await addCommentApi(id, { comment });
    if (res.success) {
      loadFeeds();
      toast.success("Comment added");
    }
  };

  const handleShare = (feed) => {
    const text = `Check out this post on Uptask by ${feed.publisher_name}:\n\n${feed.meta?.message || ""}\n\n${feed.image ? (IMAGE_BASE_URL + "/" + feed.image) : ""}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, "_blank");
  };
 
  return (
 
    <div className="min-h-screen bg-gradient-to-b from-[#0f0c29] via-[#1a0f4c] to-[#09061f] py-10 rounded-[24px]">
 
      <div className="flex justify-center">
 
        <div className="w-full max-w-[800px] px-4 text-white">
 
          <div className="max-h-[700px] overflow-y-auto no-scrollbar pr-2">
 
            {feeds.length === 0 ? (
              <div className="flex justify-center items-center h-[200px]">
                <p className="text-gray-400 text-lg">No posts yet</p>
              </div>
            ) : (
 
              feeds
                .map(f => ({ 
                  ...f, 
                  meta: (typeof f.meta === "string" && f.meta.startsWith("{")) ? JSON.parse(f.meta) : f.meta 
                }))
                .filter((feed) => {
                  if (feed.type === 'birthday_post') return false;
                  // For event-based posts, only show if event_date is TODAY
                  if (
                    feed.type === 'birthday' ||
                    feed.type === 'anniversary' ||
                    feed.type === 'anniversary_post'
                  ) {
                    if (!feed.event_date) return false;
                    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
                    return feed.event_date === today;
                  }
                  return true;
                })
                .reduce((acc, feed) => {
                  if (feed.type === "anniversary" || feed.type === "anniversary_post") {
                    const date = feed.event_date;
                    // Create a unique key based on date and involved user IDs
                    // If meta.users is missing, use the celebrant's name to ensure uniqueness per person
                    const userIds = (feed.meta?.users?.map(u => u.id) || [feed.publisher_user, feed.meta?.first_name, feed.meta?.last_name]).sort().join(',');
                    const groupKey = `anniv-${date}-${userIds}`;
                    
                    const existingIdx = acc.findIndex(item => item._groupKey === groupKey);
                    
                    if (existingIdx > -1) {
                      // Prioritize 'anniversary_post' over 'anniversary'
                      if (feed.type === "anniversary_post") {
                        acc[existingIdx] = { ...feed, _groupKey: groupKey };
                      }
                      return acc;
                    }
                    
                    acc.push({ ...feed, _groupKey: groupKey });
                    return acc;
                  }
                  acc.push(feed);
                  return acc;
                }, [])
                .map((feed) => (
 
                  feed.type === "birthday" ? (
 
 
 
<div
  key={feed.id}
  className="mb-8 w-full min-h-[362px] rounded-3xl overflow-hidden relative border border-white/10"
>
  {/* BACKGROUND */}
  <div className="absolute inset-0 bg-gradient-to-r from-[#142a7c] via-[#1f3ea8] to-[#0f1c4d]"></div>

  {/* CONTENT */}
  <div className="relative z-10 flex h-full items-center justify-between px-12 pt-10 pb-6">
    {/* LEFT TEXT */}
    <div className="max-w-[320px]">
      <p className="text-white/70 tracking-[6px] text-sm">Happy</p>
      <h1 className="text-[56px] font-extrabold text-white leading-none">BIRTHDAY</h1>
      <p className="text-white/80 text-sm mt-1 tracking-widest">YOUR MOMENT IS HERE</p>
      <p className="mt-4 text-white/80 text-xs leading-relaxed">
        Wishing you a fantastic day filled with happiness, laughter and unforgettable moments.
      </p>
      <h3 className="mt-5 text-cyan-300 text-xl font-semibold">
        {feed.meta?.first_name} {feed.meta?.last_name}
      </h3>

      {/* DATE BADGE */}
      <div className="mt-6 flex items-center gap-3">
        <div className="w-[70px] h-[70px] rounded-full border border-white/40 flex flex-col items-center justify-center text-white">
          <span className="text-xl font-bold">
            {feed.event_date ? new Date(feed.event_date).getDate() : new Date().getDate()}
          </span>
          <span className="text-[10px]">
            {feed.event_date
              ? new Date(feed.event_date).toLocaleString('default', { month: 'short' }).toUpperCase()
              : new Date().toLocaleString('default', { month: 'short' }).toUpperCase()
            }
          </span>
        </div>
        <span className="text-white/70 text-sm">Celebrate today 🎉</span>
      </div>
    </div>

    {/* PROFILE IMAGE */}
    <div className="relative">
      <div className="absolute -inset-3 rounded-full bg-white/20 blur-lg"></div>
      <img
        src={`${IMAGE_BASE_URL}${feed.publisher_profile_pic}`}
        alt="profile"
        className="relative w-[180px] h-[180px] object-cover rounded-full border-[10px] border-white/40 shadow-[0_15px_50px_rgba(0,0,0,0.8)]"
      />
    </div>
  </div>

  {/* ACTIONS & STATS */}
  <div className="relative z-10 flex items-center justify-between px-12 py-5 border-t border-white/10 bg-black/20">
    <div className="flex items-center gap-6">
      <button
        onClick={() => handleLike(feed)}
        className={`flex items-center gap-2.5 transition-all duration-300 group
        ${feed.is_liked ? "text-rose-500 scale-110" : "text-white/60 hover:text-rose-400"}`}
      >
        <Heart size={22} fill={feed.is_liked ? "currentColor" : "none"} className="group-active:scale-125 transition-transform" />
        <span className="text-sm font-semibold">{feed.total_likes || 0}</span>
      </button>

      <div className="flex items-center gap-2.5 text-white/60 hover:text-cyan-400 transition-colors cursor-default">
        <MessageCircle size={22} />
        <span className="text-sm font-semibold">{feed.comments?.length || 0}</span>
      </div>
    </div>

    <button
      onClick={() => handleShare(feed)}
      className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/5 text-white/60 hover:bg-white/10 hover:text-green-400 transition-all active:scale-95"
    >
      <Share2 size={18} />
      <span className="text-xs font-bold uppercase tracking-wider">Share</span>
    </button>
  </div>

  {/* COMMENTS SECTION */}
  <div className="relative z-10 px-12 pb-8 pt-5 border-t border-white/10 bg-black/30">
    {feed.comments?.length > 0 && (
      <div className="space-y-4 mb-5 max-h-[200px] overflow-y-auto no-scrollbar">
        {feed.comments.map((c) => (
          <div key={c.id} className="flex gap-3 group">
            <img 
              src={`${IMAGE_BASE_URL}${c.user?.profile_pic}`} 
              className="w-8 h-8 rounded-full object-cover border border-white/10" 
              alt="" 
            />
            <div className="flex-1 bg-white/5 rounded-2xl px-4 py-2.5 border border-white/5 group-hover:bg-white/[0.08] transition-colors">
              <p className="text-[13px] font-bold text-cyan-400 mb-0.5">
                {c.user?.first_name} {c.user?.last_name}
              </p>
              <p className="text-[13px] text-white/80 leading-snug">
                {c.comment}
              </p>
            </div>
          </div>
        ))}
      </div>
    )}
    <CommentBox onSubmit={(text) => handleComment(feed.id, text)} />
  </div>
</div>
 
                    ) : (feed.type === "anniversary" || feed.type === "anniversary_post") ? (
                      <div
                        key={feed.id}
                        className="mb-10 w-full min-h-[400px] rounded-[40px] overflow-hidden relative border border-white/10 shadow-[0_40px_100px_rgba(0,0,0,0.7)] group"
                      >
                        {/* PREMIUM BACKGROUND */}
                        <div className="absolute inset-0 bg-[#0f0c29]">
                          <div className="absolute top-[-10%] right-[-10%] w-[300px] h-[300px] bg-purple-600/30 rounded-full blur-[100px] animate-pulse"></div>
                          <div className="absolute bottom-[-10%] left-[-10%] w-[350px] h-[350px] bg-indigo-600/20 rounded-full blur-[120px]"></div>
                          <div className="absolute inset-0 bg-gradient-to-br from-[#1B103F] via-[#12082F] to-[#09061f]"></div>
                        </div>

                        {/* DECORATIVE ELEMENTS */}
                        <div className="absolute inset-0 overflow-hidden pointer-events-none">
                           <div className="absolute top-10 left-1/4 w-[1px] h-32 bg-gradient-to-b from-transparent via-white/20 to-transparent"></div>
                           <div className="absolute bottom-10 right-1/4 w-[1px] h-32 bg-gradient-to-b from-transparent via-white/20 to-transparent"></div>
                           <Sparkles className="absolute top-10 right-10 text-yellow-400/30 animate-twinkle" size={40} />
                        </div>

                        {/* CONTENT */}
                        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-10 pt-16 pb-10">
                          
                          <div className="mb-6 px-5 py-1.5 bg-white/5 backdrop-blur-xl rounded-full border border-white/10">
                            <p className="text-indigo-300 tracking-[8px] text-[10px] font-black uppercase">
                              {feed.meta?.day_type === 'tomorrow' ? "Tomorrow's Anniversary" : "Work Anniversary"}
                            </p>
                          </div>
                          
                          <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-8 tracking-tighter">
                            {feed.meta?.day_type === 'tomorrow' ? (
                              <>JOINING US<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400">TOMORROW</span></>
                            ) : (feed.meta?.users?.length > 1 || feed.meta?.is_combined) ? (
                              <>CELEBRATING<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400">TOGETHER</span></>
                            ) : (
                              <>YEARS OF<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400">EXCELLENCE</span></>
                            )}
                          </h1>

                          {/* AVATARS GROUP */}
                          <div className="flex -space-x-8 mb-10 items-center justify-center translate-x-4">
                            {(feed.meta?.users || []).map((emp, idx) => (
                              <div 
                                key={emp.id} 
                                className="relative transition-all duration-500 hover:scale-110 hover:-translate-y-2 hover:z-30"
                                style={{ zIndex: 10 + idx }}
                              >
                                <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 blur-md opacity-40 group-hover:opacity-70 transition-opacity"></div>
                                <img
                                  src={`${IMAGE_BASE_URL}${emp.profile_pic || feed.publisher_profile_pic || '/default-avatar.png'}`}
                                  className="relative w-28 h-28 rounded-full border-[6px] border-[#1B103F] object-cover shadow-2xl"
                                  alt={emp.first_name}
                                />
                                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-md text-white text-[10px] font-bold px-3 py-1 rounded-lg border border-white/20 shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap">
                                  {emp.first_name} {emp.last_name}
                                </div>
                              </div>
                            ))}
                            
                            {/* If no users in meta (old format or fallback) */}
                            {(!feed.meta?.users || feed.meta.users.length === 0) && (
                               <div className="relative">
                                 <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 blur-md opacity-40"></div>
                                 <img
                                   src={`${IMAGE_BASE_URL}${feed.publisher_profile_pic}`}
                                   className="relative w-28 h-28 rounded-full border-[6px] border-[#1B103F] object-cover shadow-2xl"
                                   alt=""
                                 />
                               </div>
                            )}
                          </div>

                          <div className="max-w-[550px] space-y-4">
                            <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
                              {feed.meta?.users?.length > 1 
                                ? `${feed.meta.users.map(e => e.first_name).join(' & ')}`
                                : `${feed.meta?.first_name || ''} ${feed.meta?.last_name || ''}`
                              }
                            </h2>
                            
                            <div className="inline-flex items-center gap-3 px-6 py-2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl border border-white/10">
                               <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
                               <p className="text-blue-200 text-lg font-medium italic">
                                 Completed {feed.meta?.users?.[0]?.years_completed || feed.meta?.years_completed || 1} Year milestone
                               </p>
                            </div>

                            <p className="text-white/50 text-sm leading-relaxed font-light px-4">
                              {feed.meta?.message || "Today marks a special milestone in your journey with us. Your dedication and passion have been truly inspiring!"}
                            </p>
                          </div>

                          {/* DATE BADGE */}
                          <div className="mt-12 flex flex-col items-center gap-2">
                             <div className="w-12 h-0.5 bg-gradient-to-r from-transparent via-indigo-500 to-transparent"></div>
                             <p className="text-[11px] text-white/30 font-bold tracking-[4px] uppercase">
                               {moment(feed.event_date).format("MMMM YYYY")}
                             </p>
                          </div>
                        </div>

                        {/* ACTIONS & STATS */}
                        <div className="relative z-10 flex items-center justify-between px-10 py-5 border-t border-white/10 bg-black/20">
                          <div className="flex items-center gap-6">
                            <button
                              onClick={() => handleLike(feed)}
                              className={`flex items-center gap-2.5 transition-all duration-300 group
                              ${feed.is_liked ? "text-rose-500 scale-110" : "text-white/60 hover:text-rose-400"}`}
                            >
                              <Heart size={22} fill={feed.is_liked ? "currentColor" : "none"} className="group-active:scale-125 transition-transform" />
                              <span className="text-sm font-semibold">{feed.total_likes || 0}</span>
                            </button>

                            <div className="flex items-center gap-2.5 text-white/60 hover:text-cyan-400 transition-colors cursor-default">
                              <MessageCircle size={22} />
                              <span className="text-sm font-semibold">{feed.comments?.length || 0}</span>
                            </div>
                          </div>

                          <button
                            onClick={() => handleShare(feed)}
                            className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/5 text-white/60 hover:bg-white/10 hover:text-green-400 transition-all active:scale-95"
                          >
                            <Share2 size={18} />
                            <span className="text-xs font-bold uppercase tracking-wider">Share</span>
                          </button>
                        </div>

                        {/* COMMENTS SECTION */}
                        <div className="relative z-10 px-10 pb-8 pt-5 border-t border-white/10 bg-black/30">
                          {feed.comments?.length > 0 && (
                            <div className="space-y-4 mb-5 max-h-[200px] overflow-y-auto no-scrollbar">
                              {feed.comments.map((c) => (
                                <div key={c.id} className="flex gap-3 group">
                                  <img 
                                    src={`${IMAGE_BASE_URL}${c.user?.profile_pic}`} 
                                    className="w-8 h-8 rounded-full object-cover border border-white/10" 
                                    alt="" 
                                  />
                                  <div className="flex-1 bg-white/5 rounded-2xl px-4 py-2.5 border border-white/5 group-hover:bg-white/[0.08] transition-colors">
                                    <p className="text-[13px] font-bold text-cyan-400 mb-0.5">
                                      {c.user?.first_name} {c.user?.last_name}
                                    </p>
                                    <p className="text-[13px] text-white/80 leading-snug">
                                      {c.comment}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          <CommentBox onSubmit={(text) => handleComment(feed.id, text)} />
                        </div>
                      </div>
                    ) : feed.type === "new_joiner" ? (
                      (() => {
                        const meta = typeof feed.meta === 'string' ? JSON.parse(feed.meta) : feed.meta;
                        const isFemale = (meta?.gender || '').toLowerCase() === 'female';
                       
                        return (
                          /* 🚀 NEW JOINER BANNER (ULTRA PREMIUM) */
<div
  key={feed.id}
  className="mb-8 w-full min-h-[320px] rounded-3xl overflow-hidden relative border border-white/5"
>
  {/* BACKGROUND */}
  <div className="absolute inset-0 bg-[#0f0c29] overflow-hidden">
    <div className={`absolute top-0 right-0 w-[400px] h-[400px] ${isFemale ? 'bg-pink-600/30' : 'bg-purple-600/30'} rounded-full blur-[100px] -mr-40 -mt-40 animate-pulse`}></div>
    <div className={`absolute bottom-0 left-0 w-[350px] h-[350px] ${isFemale ? 'bg-purple-600/20' : 'bg-blue-600/20'} rounded-full blur-[90px] -ml-30 -mb-30`}></div>
    <div className={`absolute inset-0 bg-gradient-to-br ${isFemale ? 'from-[#4a1a3a] via-[#1a0c2f] to-[#0a081f]' : 'from-[#1e1a5a] via-[#100c2f] to-[#0a081f]'}`}></div>
  </div>
  
  {/* DECOR */}
  <div className="absolute inset-0 pointer-events-none">
    <div className="absolute top-10 left-10 w-2 h-2 bg-white rounded-full opacity-40 animate-ping"></div>
    <div className={`absolute bottom-20 right-20 w-3 h-3 ${isFemale ? 'bg-pink-400' : 'bg-cyan-400'} rounded-full opacity-30 animate-pulse`}></div>
  </div>
  
  {/* CONTENT */}
  <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 pt-12 pb-6">
  
    {/* TAG */}
    <span className={`mb-4 px-4 py-1 bg-white/10 rounded-full text-[11px] uppercase tracking-[3px] font-bold ${isFemale ? 'text-pink-400' : 'text-cyan-400'} border border-white/10 backdrop-blur-md`}>
      ✨ New Team Member
    </span>
  
    {/* HEADING */}
    <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white leading-tight tracking-tight uppercase">
      WELCOME
      <br />
      <span className={`text-transparent bg-clip-text bg-gradient-to-r ${isFemale ? 'from-white via-pink-300 to-purple-400' : 'from-white via-cyan-300 to-blue-400'}`}>
        TO THE TEAM
      </span>
    </h1>
  
    {/* NAME + ROLE */}
    <div className="mt-6">
      <h2 className="text-2xl md:text-3xl font-bold text-white">
        {meta?.first_name} {meta?.last_name}
      </h2>
  
      <p className="mt-2 text-lg text-white/70 italic">
        {meta?.job_title || "Innovative Team Member"}
      </p>
    </div>
  </div>

  {/* ACTIONS & STATS */}
  <div className="relative z-10 flex items-center justify-between px-10 py-5 border-t border-white/10 bg-black/20">
    <div className="flex items-center gap-6">
      <button
        onClick={() => handleLike(feed)}
        className={`flex items-center gap-2.5 transition-all duration-300 group
        ${feed.is_liked ? "text-rose-500 scale-110" : "text-white/60 hover:text-rose-400"}`}
      >
        <Heart size={22} fill={feed.is_liked ? "currentColor" : "none"} className="group-active:scale-125 transition-transform" />
        <span className="text-sm font-semibold">{feed.total_likes || 0}</span>
      </button>

      <div className="flex items-center gap-2.5 text-white/60 hover:text-cyan-400 transition-colors cursor-default">
        <MessageCircle size={22} />
        <span className="text-sm font-semibold">{feed.comments?.length || 0}</span>
      </div>
    </div>

    <button
      onClick={() => handleShare(feed)}
      className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/5 text-white/60 hover:bg-white/10 hover:text-green-400 transition-all active:scale-95"
    >
      <Share2 size={18} />
      <span className="text-xs font-bold uppercase tracking-wider">Share</span>
    </button>
  </div>

  {/* COMMENTS SECTION */}
  <div className="relative z-10 px-10 pb-8 pt-5 border-t border-white/10 bg-black/30">
    {feed.comments?.length > 0 && (
      <div className="space-y-4 mb-5 max-h-[200px] overflow-y-auto no-scrollbar">
        {feed.comments.map((c) => (
          <div key={c.id} className="flex gap-3 group">
            <img 
              src={`${IMAGE_BASE_URL}${c.user?.profile_pic}`} 
              className="w-8 h-8 rounded-full object-cover border border-white/10" 
              alt="" 
            />
            <div className="flex-1 bg-white/5 rounded-2xl px-4 py-2.5 border border-white/5 group-hover:bg-white/[0.08] transition-colors">
              <p className="text-[13px] font-bold text-cyan-400 mb-0.5">
                {c.user?.first_name} {c.user?.last_name}
              </p>
              <p className="text-[13px] text-white/80 leading-snug">
                {c.comment}
              </p>
            </div>
          </div>
        ))}
      </div>
    )}
    <CommentBox onSubmit={(text) => handleComment(feed.id, text)} />
  </div>
</div>
                        );
                      })()
                    ) : feed.type === "employee_of_the_month" ? (
                      /* 🏆 EMPLOYEE OF THE MONTH (ULTRA PREMIUM) */
                      <div
                        key={feed.id}
                        className="mb-10 w-full min-h-[420px] rounded-[40px] overflow-hidden relative border border-yellow-500/20 shadow-[0_40px_100px_rgba(0,0,0,0.8)] group"
                      >
                        {/* PREMIUM BACKGROUND */}
                        <div className="absolute inset-0 bg-[#0f0c29]">
                          <div className="absolute top-[-10%] left-[-10%] w-[300px] h-[300px] bg-yellow-600/20 rounded-full blur-[100px] animate-pulse"></div>
                          <div className="absolute bottom-[-10%] right-[-10%] w-[350px] h-[350px] bg-indigo-600/30 rounded-full blur-[120px]"></div>
                          <div className="absolute inset-0 bg-gradient-to-br from-[#1B103F] via-[#12082F] to-[#09061f]"></div>
                          
                          {/* Animated Particles/Stars */}
                          <div className="absolute inset-0 opacity-30">
                            {[...Array(20)].map((_, i) => (
                              <div 
                                key={i}
                                className="absolute bg-white rounded-full animate-twinkle"
                                style={{
                                  width: Math.random() * 3 + 'px',
                                  height: Math.random() * 3 + 'px',
                                  top: Math.random() * 100 + '%',
                                  left: Math.random() * 100 + '%',
                                  animationDelay: Math.random() * 5 + 's'
                                }}
                              />
                            ))}
                          </div>
                        </div>

                        {/* CONTENT */}
                        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-10 pt-16 pb-10">
                          
                          <div className="mb-6 px-6 py-2 bg-yellow-500/10 backdrop-blur-xl rounded-full border border-yellow-500/30 shadow-[0_0_20px_rgba(234,179,8,0.2)]">
                            <p className="text-yellow-400 tracking-[8px] text-[10px] font-black uppercase flex items-center gap-3">
                              <Trophy size={14} className="fill-current" />
                              Employee of the Month
                              <Trophy size={14} className="fill-current" />
                            </p>
                          </div>
                          
                          <h1 className="text-4xl md:text-6xl font-black text-white leading-tight mb-8 tracking-tighter uppercase">
                            OUR STAR<br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-yellow-400 to-amber-500">
                              PERFORMER
                            </span>
                          </h1>

                          {/* WINNER IMAGE */}
                          <div className="relative mb-10 group/img">
                            <div className="absolute -inset-4 rounded-full bg-gradient-to-tr from-yellow-400 to-amber-600 blur-xl opacity-40 group-hover/img:opacity-80 transition-all duration-700 animate-pulse"></div>
                            <div className="relative">
                              <img
                                src={`${IMAGE_BASE_URL}${feed.meta?.winnerPic || '/default-avatar.png'}`}
                                className="w-36 h-36 rounded-full border-[8px] border-[#1B103F] object-cover shadow-2xl relative z-10 transition-transform duration-700 group-hover/img:scale-110"
                                alt={feed.meta?.winnerName}
                              />
                              <div className="absolute -right-2 -bottom-2 bg-yellow-500 text-black p-3 rounded-full shadow-xl z-20 animate-bounce">
                                <Star size={24} fill="currentColor" />
                              </div>
                            </div>
                          </div>

                          <div className="max-w-[550px] space-y-4">
                            <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight">
                              {feed.meta?.winnerName}
                            </h2>
                            
                            <div className="inline-flex items-center gap-4 px-8 py-3 bg-gradient-to-r from-yellow-600/20 to-amber-600/20 rounded-2xl border border-yellow-500/30 backdrop-blur-md">
                               <p className="text-yellow-200 text-xl font-black italic tracking-wide">
                                 {feed.meta?.monthName} {feed.meta?.year}
                               </p>
                            </div>

                            <p className="text-white/60 text-base leading-relaxed font-medium px-8 mt-4">
                              Outstanding performance, dedication, and excellence! Thank you for your incredible contribution to our success this month.
                            </p>
                          </div>
                        </div>

                        {/* ACTIONS & STATS */}
                        <div className="relative z-10 flex items-center justify-between px-10 py-5 border-t border-white/10 bg-black/20">
                          <div className="flex items-center gap-6">
                            <button
                              onClick={() => handleLike(feed)}
                              className={`flex items-center gap-2.5 transition-all duration-300 group
                              ${feed.is_liked ? "text-rose-500 scale-110" : "text-white/60 hover:text-rose-400"}`}
                            >
                              <Heart size={22} fill={feed.is_liked ? "currentColor" : "none"} className="group-active:scale-125 transition-transform" />
                              <span className="text-sm font-semibold">{feed.total_likes || 0}</span>
                            </button>

                            <div className="flex items-center gap-2.5 text-white/60 hover:text-cyan-400 transition-colors cursor-default">
                              <MessageCircle size={22} />
                              <span className="text-sm font-semibold">{feed.comments?.length || 0}</span>
                            </div>
                          </div>

                          <button
                            onClick={() => handleShare(feed)}
                            className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/5 text-white/60 hover:bg-white/10 hover:text-green-400 transition-all active:scale-95"
                          >
                            <Share2 size={18} />
                            <span className="text-xs font-bold uppercase tracking-wider">Share</span>
                          </button>
                        </div>

                        {/* COMMENTS SECTION */}
                        <div className="relative z-10 px-10 pb-8 pt-5 border-t border-white/10 bg-black/30">
                          {feed.comments?.length > 0 && (
                            <div className="space-y-4 mb-5 max-h-[200px] overflow-y-auto no-scrollbar">
                              {feed.comments.map((c) => (
                                <div key={c.id} className="flex gap-3 group">
                                  <img 
                                    src={`${IMAGE_BASE_URL}${c.user?.profile_pic}`} 
                                    className="w-8 h-8 rounded-full object-cover border border-white/10" 
                                    alt="" 
                                  />
                                  <div className="flex-1 bg-white/5 rounded-2xl px-4 py-2.5 border border-white/5 group-hover:bg-white/[0.08] transition-colors">
                                    <p className="text-[13px] font-bold text-cyan-400 mb-0.5">
                                      {c.user?.first_name} {c.user?.last_name}
                                    </p>
                                    <p className="text-[13px] text-white/80 leading-snug">
                                      {c.comment}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          <CommentBox onSubmit={(text) => handleComment(feed.id, text)} />
                        </div>
                      </div>
                    ) : (
 
                    /* NORMAL POST */
                    <div
                      key={feed.id}
                      className="
                      mb-10 rounded-[32px] bg-gradient-to-br from-[#1e174a]/80 to-[#0f0c29]/90 backdrop-blur-2xl
                      border border-white/10 shadow-[0_25px_60px_rgba(0,0,0,0.5)]
                      overflow-hidden transition-all duration-300 hover:shadow-[0_35px_80px_rgba(0,0,0,0.7)] hover:translate-y-[-4px]
                      "
                    >
                      {/* HEADER */}
                      <div className="flex items-center justify-between px-7 py-6">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <img
                              src={`${IMAGE_BASE_URL}${feed.publisher_profile_pic}`}
                              className="w-12 h-12 rounded-full object-cover border-2 border-purple-500/50 shadow-lg"
                              alt=""
                            />
                            <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-[#1e174a] rounded-full"></div>
                          </div>
                          <div>
                            <p className="font-bold text-white text-[15px] tracking-wide">
                              {feed.publisher_name}
                            </p>
                            <p className="text-[11px] text-white/40 font-medium uppercase tracking-tighter">
                               {moment(feed.created_at).format("DD MMM YYYY")}
                            </p>
                          </div>
                        </div>

                        <button
                          onClick={() => handleDelete(feed.id)}
                          className="p-2 rounded-full hover:bg-white/5 text-white/30 hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>

                      {/* MESSAGE */}
                      {feed.meta?.message && (
                        <div className="px-7 pb-4">
                          <div className="text-[16px] text-white/90 leading-relaxed break-words font-light">
                            {feed.meta.message.split("\n").map((line, i) => (
                              <span key={i}>
                                {line}
                                <br />
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* IMAGE */}
                      {feed.image && (
                        <div className="px-5 pb-6">
                          <div className="rounded-[24px] overflow-hidden bg-black/40 border border-white/5 shadow-inner">
                            <img
                              src={`${IMAGE_BASE_URL}/${feed.image}`}
                              className="w-full max-h-[600px] object-contain mx-auto transition-transform duration-700 hover:scale-[1.02]"
                              alt=""
                            />
                          </div>
                        </div>
                      )}

                      {/* ACTIONS & STATS */}
                      <div className="flex items-center justify-between px-7 py-5 border-t border-white/5 bg-white/[0.02]">
                        <div className="flex items-center gap-6">
                          <button
                            onClick={() => handleLike(feed)}
                            className={`flex items-center gap-2.5 transition-all duration-300 group
                            ${feed.is_liked ? "text-rose-500 scale-110" : "text-white/60 hover:text-rose-400"}`}
                          >
                            <Heart size={22} fill={feed.is_liked ? "currentColor" : "none"} className="group-active:scale-125 transition-transform" />
                            <span className="text-sm font-semibold">{feed.total_likes || 0}</span>
                          </button>

                          <div className="flex items-center gap-2.5 text-white/60 hover:text-cyan-400 transition-colors cursor-default">
                            <MessageCircle size={22} />
                            <span className="text-sm font-semibold">{feed.comments?.length || 0}</span>
                          </div>
                        </div>

                        <button
                          onClick={() => handleShare(feed)}
                          className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/5 text-white/60 hover:bg-white/10 hover:text-green-400 transition-all active:scale-95"
                        >
                          <Share2 size={18} />
                          <span className="text-xs font-bold uppercase tracking-wider">Share</span>
                        </button>
                      </div>

                      {/* COMMENTS SECTION */}
                      <div className="px-7 pb-6 pt-5 border-t border-white/5 bg-black/10">
                        {feed.comments?.length > 0 && (
                          <div className="space-y-4 mb-5">
                            {feed.comments.map((c) => (
                              <div key={c.id} className="flex gap-3 group">
                                <img 
                                  src={`${IMAGE_BASE_URL}${c.user?.profile_pic}`} 
                                  className="w-8 h-8 rounded-full object-cover border border-white/10" 
                                  alt="" 
                                />
                                <div className="flex-1 bg-white/5 rounded-2xl px-4 py-2.5 border border-white/5 group-hover:bg-white/[0.08] transition-colors">
                                  <p className="text-[13px] font-bold text-cyan-400 mb-0.5">
                                    {c.user?.first_name} {c.user?.last_name}
                                  </p>
                                  <p className="text-[13px] text-white/80 leading-snug">
                                    {c.comment}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        <CommentBox onSubmit={(text) => handleComment(feed.id, text)} />
                      </div>
                    </div>
                  )
 
                ))
 
            )}
 
          </div>
 
        </div>
 
      </div>
 
      <CreatePostModal
        showModal={showModal}
        setShowModal={setShowModal}
        onPostCreated={loadFeeds}
      />
 
    </div>
  );
}
 
 
/* COMMENT BOX */
function CommentBox({ onSubmit }) {
  const [text, setText] = useState("");

  const handleSend = () => {
    if (!text.trim()) return;
    onSubmit(text);
    setText("");
  };

  return (
    <div className="flex gap-3 mt-4">
      <div className="flex-1 relative group">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
          placeholder="Write a comment..."
          className="w-full bg-white/5 border border-white/10 px-5 py-3 rounded-2xl text-[14px] text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
        />
      </div>

      <button
        onClick={handleSend}
        disabled={!text.trim()}
        className="bg-cyan-600 p-3 rounded-2xl text-white hover:bg-cyan-500 disabled:opacity-40 disabled:hover:bg-cyan-600 transition-all active:scale-90 flex items-center justify-center shadow-lg shadow-cyan-900/20"
      >
        <Send size={20} />
      </button>
    </div>
  );
}



// ==============================



// import { useEffect, useState, useMemo } from "react";
// import {
//   getFeedsApi,
//   deleteFeedApi,
//   likeFeedApi,
//   unlikeFeedApi,
//   addCommentApi,
// } from "../../app/api";
// import { 
//   Heart, 
//   MessageCircle, 
//   Share2, 
//   Send, 
//   Trash2,
//   Calendar,
//   Sparkles,
//   Trophy,
//   Star,
//   Search,
//   Filter,
//   Plus,
//   ChevronLeft,
//   ChevronRight,
//   User,
//   FileText,
//   Clock,
//   Eye,
//   CheckCircle,
//   MoreVertical,
//   CheckCircle2,
//   Edit2
// } from "lucide-react";
// import CreatePostModal from "./CreatePostModal";
// import { toast } from "react-hot-toast";
// import moment from "moment";
// import { motion, AnimatePresence } from "framer-motion";

// const IMAGE_BASE_URL = "https://uptask.visiontrek.io";

// export default function CreateFeed() {
//   const [feeds, setFeeds] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [activeFilter, setActiveFilter] = useState("All"); // All, Published, Drafts, Scheduled
//   const [selectedFeedId, setSelectedFeedId] = useState(null);
//   const [currentPage, setCurrentPage] = useState(1);
//   const [showPostModal, setShowPostModal] = useState(false);
//   const [commentText, setCommentText] = useState("");

//   const user = JSON.parse(localStorage.getItem("user"));
//   const isHR = Number(user?.role_id) === 2;

//   // Exact 6 posts matching the list and content in the user's screenshot
//   const targetMockPosts = useMemo(() => [
//     {
//       id: "welcome-uptask-platform",
//       type: "post",
//       publisher_name: "Ashok Kumar",
//       publisher_profile_pic: "/uploads/profile_pics/default.png",
//       publisher_role: "Admin",
//       created_at: "2025-05-18T10:30:00.000Z",
//       status: "Published",
//       views: 124,
//       meta: {
//         message: "We are excited to officially introduce Uptask Platform — your all-in-one solution to manage projects, tasks, attendance, leaves, payroll and more!\n\nOur mission is to simplify the way teams work together and help organizations achieve more every day."
//       },
//       comments: [
//         {
//           id: "c-1",
//           comment: "This new platform design is absolutely amazing! Love the dark theme.",
//           created_at: "2025-05-18T11:00:00.000Z",
//           user: { first_name: "Nitin", last_name: "Jha", profile_pic: "" }
//         },
//         {
//           id: "c-2",
//           comment: "Automated payroll makes life so much easier. Great job!",
//           created_at: "2025-05-18T11:30:00.000Z",
//           user: { first_name: "Anjali", last_name: "Sharma", profile_pic: "" }
//         }
//       ],
//       total_likes: 24,
//       is_liked: false,
//       isWelcome: true
//     },
//     {
//       id: "new-feature-release",
//       type: "post",
//       publisher_name: "Ashok Kumar",
//       publisher_profile_pic: "/uploads/profile_pics/default.png",
//       publisher_role: "Admin",
//       created_at: "2025-05-16T14:15:00.000Z",
//       status: "Published",
//       views: 95,
//       meta: {
//         message: "New Feature Release: Sprint Planning & Burndown Charts!\n\nWe have updated the Tasks module to include sprint cycles and real-time burndown analytics. Managers can now create sprints, allocate points, and track completion progress with interactive graphs."
//       },
//       comments: [],
//       total_likes: 12,
//       is_liked: false
//     },
//     {
//       id: "system-maintenance-notice",
//       type: "post",
//       publisher_name: "Ashok Kumar",
//       publisher_profile_pic: "/uploads/profile_pics/default.png",
//       publisher_role: "Admin",
//       created_at: "2025-05-15T11:00:00.000Z",
//       status: "Draft",
//       views: 0,
//       meta: {
//         message: "System Maintenance Notice\n\nPlease be advised that the Uptask platform will undergo scheduled system maintenance this Sunday from 2:00 AM to 5:00 AM GMT. During this window, some services may be temporarily offline.\n\nThank you for your patience."
//       },
//       comments: [],
//       total_likes: 0,
//       is_liked: false
//     },
//     {
//       id: "tips-productivity",
//       type: "post",
//       publisher_name: "Ashok Kumar",
//       publisher_profile_pic: "/uploads/profile_pics/default.png",
//       publisher_role: "Admin",
//       created_at: "2025-05-20T09:00:00.000Z",
//       status: "Scheduled",
//       views: 8,
//       meta: {
//         message: "Tips for Better Productivity\n\nHere are some simple tips to boost your focus during work hours:\n\n1. Use the Pomodoro Technique (25m work, 5m break).\n2. Block distracting websites using focus apps.\n3. Take regular short walks to refresh your mind.\n4. Clear your desk of unnecessary items.\n\nLet us know what works best for you!"
//       },
//       comments: [],
//       total_likes: 3,
//       is_liked: false
//     },
//     {
//       id: "holiday-announcement",
//       type: "post",
//       publisher_name: "Ashok Kumar",
//       publisher_profile_pic: "/uploads/profile_pics/default.png",
//       publisher_role: "Admin",
//       created_at: "2025-05-14T16:45:00.000Z",
//       status: "Published",
//       views: 78,
//       meta: {
//         message: "Holiday Announcement: Memorial Day Weekend!\n\nPlease note that our offices will be closed on Monday, May 26th in observance of Memorial Day. Regular business hours will resume on Tuesday.\n\nHave a safe and relaxing holiday weekend!"
//       },
//       comments: [],
//       total_likes: 19,
//       is_liked: false
//     },
//     {
//       id: "team-building-event",
//       type: "post",
//       publisher_name: "Ashok Kumar",
//       publisher_profile_pic: "/uploads/profile_pics/default.png",
//       publisher_role: "Admin",
//       created_at: "2025-05-13T15:30:00.000Z",
//       status: "Draft",
//       views: 0,
//       meta: {
//         message: "Team Building Event Suggestions\n\nWe are looking to organize an outdoor team outing next month! Please share your favorite ideas in the comments below."
//       },
//       comments: [],
//       total_likes: 0,
//       is_liked: false
//     }
//   ], []);

//   useEffect(() => {
//     loadFeeds();
//   }, []);

//   const loadFeeds = async () => {
//     setLoading(true);
//     try {
//       const res = await getFeedsApi();
//       if (res.success) {
//         setFeeds(res.data);
//       }
//     } catch (e) {
//       toast.error("Failed to load feeds");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleDelete = async (id) => {
//     if (typeof id === 'string' && (id.startsWith("welcome-") || id.startsWith("new-") || id.startsWith("system-") || id.startsWith("tips-") || id.startsWith("holiday-") || id.startsWith("team-"))) {
//       toast.success("Mock post removed");
//       return;
//     }
//     const res = await deleteFeedApi(id);
//     if (res.success) {
//       toast.success("Post deleted successfully");
//       loadFeeds();
//       if (selectedFeedId === id) {
//         setSelectedFeedId(null);
//       }
//     } else {
//       toast.error("Failed to delete post");
//     }
//   };

//   const handleLike = async (feed) => {
//     if (typeof feed.id === 'string' && (feed.id.startsWith("welcome-") || feed.id.startsWith("new-") || feed.id.startsWith("system-") || feed.id.startsWith("tips-") || feed.id.startsWith("holiday-") || feed.id.startsWith("team-"))) {
//       toast.success("Liked post!");
//       return;
//     }
//     const updatedFeeds = feeds.map(f => {
//       if (f.id === feed.id) {
//         return {
//           ...f,
//           is_liked: !f.is_liked,
//           total_likes: f.is_liked ? (f.total_likes - 1) : (f.total_likes + 1)
//         };
//       }
//       return f;
//     });
//     setFeeds(updatedFeeds);

//     const res = feed.is_liked ? await unlikeFeedApi(feed.id) : await likeFeedApi(feed.id);
//     if (!res.success) {
//       loadFeeds();
//       toast.error("Failed to update like");
//     }
//   };

//   const handleCommentSubmit = async (e) => {
//     e.preventDefault();
//     if (!commentText.trim() || !selectedFeedId) return;

//     if (typeof selectedFeedId === 'string' && (selectedFeedId.startsWith("welcome-") || selectedFeedId.startsWith("new-") || selectedFeedId.startsWith("system-") || selectedFeedId.startsWith("tips-") || selectedFeedId.startsWith("holiday-") || selectedFeedId.startsWith("team-"))) {
//       toast.success("Comment added!");
//       setCommentText("");
//       return;
//     }

//     const res = await addCommentApi(selectedFeedId, { comment: commentText });
//     if (res.success) {
//       loadFeeds();
//       setCommentText("");
//       toast.success("Comment added");
//     } else {
//       toast.error("Failed to add comment");
//     }
//   };

//   const handleShare = (feed) => {
//     const text = `Check out this post on Uptask by ${feed.publisher_name}:\n\n${feed.meta?.message || ""}\n\n${feed.image ? (IMAGE_BASE_URL + "/" + feed.image) : ""}`;
//     const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
//     window.open(whatsappUrl, "_blank");
//   };

//   const parsedFeeds = useMemo(() => {
//     const apiFeeds = feeds.map(f => ({
//       ...f,
//       status: "Published",
//       views: f.views || Math.floor((f.total_likes || 0) * 4.8) + 15,
//       meta: (typeof f.meta === "string" && f.meta.startsWith("{")) ? JSON.parse(f.meta) : f.meta
//     })).filter(feed => {
//       if (feed.type === 'birthday_post') return false;
//       return true;
//     });

//     return [...targetMockPosts, ...apiFeeds].sort((a, b) => 
//       new Date(b.created_at) - new Date(a.created_at)
//     );
//   }, [feeds, targetMockPosts]);

//   const stats = useMemo(() => {
//     const published = parsedFeeds.filter(f => f.status === "Published").length;
//     const drafts = parsedFeeds.filter(f => f.status === "Draft").length;
//     const scheduled = parsedFeeds.filter(f => f.status === "Scheduled").length;
//     return {
//       total: parsedFeeds.length,
//       published,
//       drafts,
//       scheduled
//     };
//   }, [parsedFeeds]);

//   const filteredFeeds = useMemo(() => {
//     return parsedFeeds.filter(feed => {
//       const msg = (feed.meta?.message || feed.type || "").toLowerCase();
//       const pubName = (feed.publisher_name || "").toLowerCase();
//       const matchesSearch = msg.includes(searchTerm.toLowerCase()) || pubName.includes(searchTerm.toLowerCase());

//       if (activeFilter === "All") return matchesSearch;
//       if (activeFilter === "Published") return matchesSearch && feed.status === "Published";
//       if (activeFilter === "Drafts") return matchesSearch && feed.status === "Draft";
//       if (activeFilter === "Scheduled") return matchesSearch && feed.status === "Scheduled";
//       return matchesSearch;
//     });
//   }, [parsedFeeds, searchTerm, activeFilter]);

//   const itemsPerPage = 6;
//   const totalPages = Math.ceil(filteredFeeds.length / itemsPerPage) || 1;
//   const paginatedFeeds = useMemo(() => {
//     const start = (currentPage - 1) * itemsPerPage;
//     return filteredFeeds.slice(start, start + itemsPerPage);
//   }, [filteredFeeds, currentPage]);

//   const selectedFeed = useMemo(() => {
//     if (!selectedFeedId && filteredFeeds.length > 0) {
//       return filteredFeeds[0];
//     }
//     return filteredFeeds.find(f => f.id === selectedFeedId) || filteredFeeds[0];
//   }, [selectedFeedId, filteredFeeds]);

//   useEffect(() => {
//     setCurrentPage(1);
//   }, [searchTerm, activeFilter]);

//   useEffect(() => {
//     if (selectedFeed) {
//       setSelectedFeedId(selectedFeed.id);
//     }
//   }, [selectedFeed]);

//   const getPostTitle = (feed) => {
//     if (!feed) return "Untitled";
//     if (feed.type === 'birthday') return `Happy Birthday: ${feed.meta?.first_name || ''} ${feed.meta?.last_name || ''}!`;
//     if (feed.type === 'anniversary' || feed.type === 'anniversary_post') {
//       const firstUser = feed.meta?.users?.[0];
//       const name = firstUser ? `${firstUser.first_name} ${firstUser.last_name}` : `${feed.meta?.first_name || ''} ${feed.meta?.last_name || ''}`;
//       return `Work Anniversary: ${name}!`;
//     }
//     if (feed.type === 'new_joiner') return `Welcome Team Member: ${feed.meta?.first_name || ''} ${feed.meta?.last_name || ''}!`;
//     if (feed.type === 'employee_of_the_month') return `Star Performer: ${feed.meta?.winnerName || ''}!`;
//     return feed.meta?.message?.split("\n")[0] || "No message content";
//   };

//   const getStatusBadge = (status) => {
//     switch (status) {
//       case "Published":
//         return (
//           <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 shadow-[0_0_8px_rgba(16,185,129,0.1)]">
//             <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse"></span>
//             Published
//           </span>
//         );
//       case "Draft":
//         return (
//           <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black bg-amber-500/10 text-amber-400 border border-amber-500/20">
//             <span className="w-1 h-1 rounded-full bg-amber-400"></span>
//             Draft
//           </span>
//         );
//       case "Scheduled":
//         return (
//           <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
//             <span className="w-1 h-1 rounded-full bg-indigo-400"></span>
//             Scheduled
//           </span>
//         );
//       default:
//         return null;
//     }
//   };

//   const getWelcomeBanner = () => (
//     <div className="w-full bg-gradient-to-r from-[#170e3a] via-[#221650] to-[#12082F] rounded-2xl p-5 border border-white/10 shadow-[0_8px_25px_rgba(0,0,0,0.3)] flex flex-col items-center justify-center text-center relative overflow-hidden my-3 min-h-[180px]">
//       <div className="absolute -top-10 -left-10 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl"></div>
//       <div className="absolute -bottom-10 -right-10 w-24 h-24 bg-purple-500/10 rounded-full blur-2xl"></div>
      
//       <h2 className="text-xl font-black tracking-tight text-white mb-0.5">
//         One Platform.
//       </h2>
//       <h3 className="text-lg font-bold tracking-tight text-indigo-300 mb-2">
//         All Solutions.
//       </h3>
//       <p className="text-white/60 text-[10px] max-w-sm mb-4 leading-relaxed">
//         Streamline your work, boost productivity and achieve more with Uptask.
//       </p>

//       {/* Module Badges */}
//       <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full max-w-md">
//         <div className="bg-white/5 border border-white/5 rounded-xl p-1.5 flex items-center gap-2">
//           <div className="w-5 h-5 rounded-lg bg-purple-500/20 flex items-center justify-center text-purple-400 text-[10px] shrink-0 font-black">P</div>
//           <div className="text-left min-w-0">
//             <p className="text-[7px] text-white/30 uppercase font-black tracking-wider leading-none">Projects</p>
//             <p className="text-[9px] text-white font-bold leading-none mt-0.5">24 Active</p>
//           </div>
//         </div>
//         <div className="bg-white/5 border border-white/5 rounded-xl p-1.5 flex items-center gap-2">
//           <div className="w-5 h-5 rounded-lg bg-orange-500/20 flex items-center justify-center text-orange-400 text-[10px] shrink-0 font-black">T</div>
//           <div className="text-left min-w-0">
//             <p className="text-[7px] text-white/30 uppercase font-black tracking-wider leading-none">Tasks</p>
//             <p className="text-[9px] text-white font-bold leading-none mt-0.5">128 Pending</p>
//           </div>
//         </div>
//         <div className="bg-white/5 border border-white/5 rounded-xl p-1.5 flex items-center gap-2">
//           <div className="w-5 h-5 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-[10px] shrink-0 font-black">A</div>
//           <div className="text-left min-w-0">
//             <p className="text-[7px] text-white/30 uppercase font-black tracking-wider leading-none">Attendance</p>
//             <p className="text-[9px] text-white font-bold leading-none mt-0.5">98% Present</p>
//           </div>
//         </div>
//         <div className="bg-white/5 border border-white/5 rounded-xl p-1.5 flex items-center gap-2">
//           <div className="w-5 h-5 rounded-lg bg-blue-500/20 flex items-center justify-center text-blue-400 text-[10px] shrink-0 font-black">L</div>
//           <div className="text-left min-w-0">
//             <p className="text-[7px] text-white/30 uppercase font-black tracking-wider leading-none">Leaves</p>
//             <p className="text-[9px] text-white font-bold leading-none mt-0.5">12 Pending</p>
//           </div>
//         </div>
//       </div>
//     </div>
//   );

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-[#0e0724] via-[#12082F] to-[#080316] text-white p-4 sm:p-6 font-sans">
//       <div className="max-w-[1600px] mx-auto space-y-6">

//         {/* 1. TOP KPI SUMMARY PANEL */}
//         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
//           {/* Total Posts */}
//           <div 
//             onClick={() => setActiveFilter("All")}
//             className={`cursor-pointer bg-gradient-to-br from-[#1c0e48]/60 to-[#0e0626]/70 border p-4 rounded-2xl flex items-center justify-between shadow-[0_8px_32px_0_rgba(124,58,237,0.06)] backdrop-blur-xl transition-all duration-300 hover:scale-[1.02] ${
//               activeFilter === "All" ? "border-purple-500/60 shadow-purple-500/20 bg-[#1c0e48]/90" : "border-white/5"
//             }`}
//           >
//             <div className="flex items-center gap-3">
//               <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center text-purple-400 border border-purple-500/30">
//                 <FileText size={18} />
//               </div>
//               <div>
//                 <p className="text-white/40 text-[9px] uppercase font-black tracking-wider leading-none">Total Posts</p>
//                 <h4 className="text-xl font-bold mt-1 leading-none">{stats.total}</h4>
//               </div>
//             </div>
//             <span className="text-[9px] font-bold text-emerald-400 px-2.5 py-0.5 rounded-full bg-emerald-500/20">↑ 12%</span>
//           </div>

//           {/* Published */}
//           <div 
//             onClick={() => setActiveFilter("Published")}
//             className={`cursor-pointer bg-gradient-to-br from-[#1c0e48]/60 to-[#0e0626]/70 border p-4 rounded-2xl flex items-center justify-between shadow-[0_8px_32px_0_rgba(16,185,129,0.06)] backdrop-blur-xl transition-all duration-300 hover:scale-[1.02] ${
//               activeFilter === "Published" ? "border-emerald-500/60 shadow-emerald-500/20 bg-[#1c0e48]/90" : "border-white/5"
//             }`}
//           >
//             <div className="flex items-center gap-3">
//               <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center text-emerald-400 border border-emerald-500/30">
//                 <CheckCircle2 size={18} />
//               </div>
//               <div>
//                 <p className="text-white/40 text-[9px] uppercase font-black tracking-wider leading-none">Published</p>
//                 <h4 className="text-xl font-bold mt-1 leading-none">{stats.published}</h4>
//               </div>
//             </div>
//             <span className="text-[9px] font-bold text-purple-400 px-2.5 py-0.5 rounded-full bg-purple-500/20">↑ 8%</span>
//           </div>

//           {/* Drafts */}
//           <div 
//             onClick={() => setActiveFilter("Drafts")}
//             className={`cursor-pointer bg-gradient-to-br from-[#1c0e48]/60 to-[#0e0626]/70 border p-4 rounded-2xl flex items-center justify-between shadow-[0_8px_32px_0_rgba(245,158,11,0.06)] backdrop-blur-xl transition-all duration-300 hover:scale-[1.02] ${
//               activeFilter === "Drafts" ? "border-amber-500/60 shadow-amber-500/20 bg-[#1c0e48]/90" : "border-white/5"
//             }`}
//           >
//             <div className="flex items-center gap-3">
//               <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center text-amber-400 border border-amber-500/30">
//                 <FileText size={18} />
//               </div>
//               <div>
//                 <p className="text-white/40 text-[9px] uppercase font-black tracking-wider leading-none">Drafts</p>
//                 <h4 className="text-xl font-bold mt-1 leading-none">{stats.drafts}</h4>
//               </div>
//             </div>
//             <span className="text-[9px] font-bold text-white/40 px-2.5 py-0.5 rounded-full bg-white/10">Local</span>
//           </div>

//           {/* Scheduled */}
//           <div 
//             onClick={() => setActiveFilter("Scheduled")}
//             className={`cursor-pointer bg-gradient-to-br from-[#1c0e48]/60 to-[#0e0626]/70 border p-4 rounded-2xl flex items-center justify-between shadow-[0_8px_32px_0_rgba(99,102,241,0.06)] backdrop-blur-xl transition-all duration-300 hover:scale-[1.02] ${
//               activeFilter === "Scheduled" ? "border-indigo-500/60 shadow-indigo-500/20 bg-[#1c0e48]/90" : "border-white/5"
//             }`}
//           >
//             <div className="flex items-center gap-3">
//               <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center text-indigo-400 border border-indigo-500/30">
//                 <Clock size={18} />
//               </div>
//               <div>
//                 <p className="text-white/40 text-[9px] uppercase font-black tracking-wider leading-none">Scheduled</p>
//                 <h4 className="text-xl font-bold mt-1 leading-none">{stats.scheduled}</h4>
//               </div>
//             </div>
//             <span className="text-[9px] font-bold text-indigo-400 px-2.5 py-0.5 rounded-full bg-indigo-500/20">↑ 5%</span>
//           </div>

//           {/* + Create Post Button Card */}
//           <button 
//             onClick={() => setShowPostModal(true)}
//             className="col-span-1 bg-gradient-to-r from-violet-600 via-indigo-600 to-cyan-600 border border-white/10 p-4 rounded-2xl flex items-center justify-center gap-2 shadow-[0_8px_24px_rgba(99,102,241,0.25)] hover:shadow-[0_8px_32px_rgba(99,102,241,0.4)] hover:scale-[1.02] active:scale-95 transition-all text-white text-sm font-black"
//           >
//             <Plus size={18} />
//             Create Post
//           </button>
//         </div>

//         {/* 2. DUAL COLUMN LAYOUT */}
//         <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

//           {/* LEFT COLUMN: LIST */}
//           <div className="lg:col-span-5 bg-gradient-to-b from-[#190d3e]/50 to-[#0c051f]/75 border border-white/10 rounded-3xl p-5 shadow-2xl backdrop-blur-xl flex flex-col justify-between min-h-[600px]">
//             <div>
//               <div className="mb-4">
//                 <h3 className="text-lg font-black tracking-tight text-white/90 flex items-center gap-2">
//                   Posts
//                 </h3>
//                 <p className="text-white/40 text-[11px]">Manage and organize all your posts</p>
//               </div>

//               {/* SEARCH & FILTERS */}
//               <div className="flex gap-2 mb-4">
//                 <div className="relative flex-1">
//                   <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
//                   <input
//                     type="text"
//                     value={searchTerm}
//                     onChange={(e) => setSearchTerm(e.target.value)}
//                     placeholder="Search posts..."
//                     className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-3 py-2.5 text-xs text-white focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/35 transition placeholder-white/20"
//                   />
//                 </div>
//                 <button 
//                   className="bg-white/5 border border-white/10 hover:bg-white/10 px-3.5 py-2.5 rounded-xl text-xs font-black flex items-center gap-1.5 transition text-white/70 hover:text-white"
//                   onClick={() => {
//                     const filters = ["All", "Published", "Drafts", "Scheduled"];
//                     const nextIdx = (filters.indexOf(activeFilter) + 1) % filters.length;
//                     setActiveFilter(filters[nextIdx]);
//                   }}
//                 >
//                   <Filter size={12} />
//                   {activeFilter}
//                 </button>
//               </div>

//               {/* LIST ITEMS */}
//               {loading ? (
//                 <div className="flex justify-center items-center py-20">
//                   <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
//                 </div>
//               ) : paginatedFeeds.length === 0 ? (
//                 <div className="text-center py-20 text-white/20">
//                   <FileText size={24} className="mx-auto mb-2 opacity-20" />
//                   <p className="text-xs">No posts matched</p>
//                 </div>
//               ) : (
//                 <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
//                   {paginatedFeeds.map((feed) => {
//                     const isActive = selectedFeedId === feed.id;
//                     const title = getPostTitle(feed);
//                     const isBday = feed.type === 'birthday';
//                     const hasPic = feed.publisher_profile_pic;
                    
//                     return (
//                       <div
//                         key={feed.id}
//                         onClick={() => setSelectedFeedId(feed.id)}
//                         className={`cursor-pointer p-3.5 rounded-2xl border transition-all duration-300 flex items-center gap-3 hover:-translate-y-0.5 ${
//                           isActive 
//                             ? "bg-gradient-to-r from-purple-500/15 via-[#1c0f47]/40 to-transparent border-l-4 border-l-purple-500 border-t-purple-500/20 border-r-purple-500/20 border-b-purple-500/20 shadow-[0_8px_20px_rgba(124,58,237,0.08)]" 
//                             : "bg-white/5 border-white/5 hover:bg-white/[0.08] border-l-transparent"
//                         }`}
//                       >
//                         {/* Thumbnail image or placeholder */}
//                         <div className="w-10 h-10 rounded-xl overflow-hidden bg-white/5 flex items-center justify-center shrink-0 border border-white/10">
//                           {isBday && hasPic ? (
//                             <img src={`${IMAGE_BASE_URL}${feed.publisher_profile_pic}`} className="w-full h-full object-cover" alt="thumb" />
//                           ) : feed.image ? (
//                             <img src={`${IMAGE_BASE_URL}/${feed.image}`} className="w-full h-full object-cover" alt="thumb" />
//                           ) : (
//                             <span className="text-white/30 text-xs">📄</span>
//                           )}
//                         </div>

//                         {/* Details */}
//                         <div className="flex-1 min-w-0">
//                           <h4 className="text-xs font-black text-white leading-snug truncate">{title}</h4>
//                           <span className="text-[10px] text-white/30 font-medium mt-1 inline-block">
//                             {moment(feed.created_at).format("MMM DD, YYYY - hh:mm A")}
//                           </span>
//                         </div>

//                         {/* Status badge */}
//                         <div className="shrink-0">
//                           {getStatusBadge(feed.status)}
//                         </div>
//                       </div>
//                     );
//                   })}
//                 </div>
//               )}
//             </div>

//             {/* PAGINATION PANEL */}
//             {filteredFeeds.length > itemsPerPage && (
//               <div className="flex items-center justify-between border-t border-white/5 pt-4 mt-4">
//                 <span className="text-[10px] text-white/40 font-black uppercase tracking-wider">
//                   Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredFeeds.length)} of {filteredFeeds.length}
//                 </span>
//                 <div className="flex gap-1.5">
//                   <button
//                     onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
//                     disabled={currentPage === 1}
//                     className="p-2 bg-white/5 hover:bg-white/10 rounded-xl text-white/70 disabled:opacity-30 transition"
//                   >
//                     <ChevronLeft size={14} />
//                   </button>
//                   <button
//                     onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
//                     disabled={currentPage === totalPages}
//                     className="p-2 bg-white/5 hover:bg-white/10 rounded-xl text-white/70 disabled:opacity-30 transition"
//                   >
//                     <ChevronRight size={14} />
//                   </button>
//                 </div>
//               </div>
//             )}
//           </div>

//           {/* RIGHT COLUMN: PREVIEW PANEL */}
//           <div className="lg:col-span-7 bg-gradient-to-b from-[#190d3e]/50 to-[#0c051f]/75 border border-white/10 rounded-3xl p-6 shadow-2xl backdrop-blur-xl flex flex-col justify-start gap-4">
//             {selectedFeed ? (
//               <div className="flex flex-col gap-4">
//                 <div>
//                   {/* Category Header */}
//                   <div className="flex items-center justify-between mb-4 border-b border-white/5 pb-3">
//                     <span className="px-3.5 py-1 bg-purple-500/20 text-purple-300 border border-purple-500/30 rounded-full text-[9px] font-black uppercase tracking-widest shadow-[0_0_12px_rgba(168,85,247,0.1)]">
//                       {selectedFeed.type === "post" ? "Announcement" : selectedFeed.type}
//                     </span>
//                     <div className="flex items-center gap-2">
//                       {getStatusBadge(selectedFeed.status)}
//                     </div>
//                   </div>

//                   {/* Details content */}
//                   <div className="space-y-4">
//                     {selectedFeed.type === "birthday" ? (
//                       /* BIRTHDAY VIEW */
//                       <div className="w-full rounded-2xl overflow-hidden relative border border-white/10 p-6 bg-gradient-to-r from-[#142a7c] via-[#1f3ea8] to-[#0f1c4d]">
//                         <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
//                           <div className="text-center sm:text-left">
//                             <p className="text-white/60 tracking-[4px] text-[10px] uppercase font-black">Happy</p>
//                             <h2 className="text-3xl font-extrabold text-white leading-none tracking-tight">BIRTHDAY</h2>
//                             <p className="text-cyan-300 text-lg font-bold mt-2">
//                               {selectedFeed.meta?.first_name} {selectedFeed.meta?.last_name}
//                             </p>
//                             <p className="text-white/80 text-[11px] mt-2 max-w-[280px] leading-relaxed">
//                               Wishing you a fantastic day filled with happiness, laughter and unforgettable moments.
//                             </p>
//                           </div>
//                           <img
//                             src={`${IMAGE_BASE_URL}${selectedFeed.publisher_profile_pic || "/default-avatar.png"}`}
//                             className="w-24 h-24 object-cover rounded-full border-4 border-white/30 shadow-xl shrink-0"
//                             alt="avatar"
//                           />
//                         </div>
//                       </div>
//                     ) : (selectedFeed.type === "anniversary" || selectedFeed.type === "anniversary_post") ? (
//                       /* ANNIVERSARY VIEW */
//                       <div className="w-full rounded-2xl overflow-hidden relative border border-white/10 p-6 bg-gradient-to-br from-[#1B103F] via-[#12082F] to-[#09061f] text-center flex flex-col items-center">
//                         <span className="text-[9px] font-black uppercase text-indigo-400 tracking-[6px] mb-1">Work Anniversary</span>
//                         <h2 className="text-2xl font-black text-white leading-tight mb-5">YEARS OF EXCELLENCE</h2>
                        
//                         <div className="flex -space-x-3 mb-4">
//                           {(selectedFeed.meta?.users || []).map((emp, idx) => (
//                             <img
//                               key={emp.id || idx}
//                               src={`${IMAGE_BASE_URL}${emp.profile_pic || selectedFeed.publisher_profile_pic || '/default-avatar.png'}`}
//                               className="w-16 h-16 rounded-full border-4 border-[#12082F] object-cover shadow-lg relative animate-pulse"
//                               style={{ zIndex: 10 + idx }}
//                               alt="avatar"
//                             />
//                           ))}
//                           {(!selectedFeed.meta?.users || selectedFeed.meta.users.length === 0) && (
//                             <img
//                               src={`${IMAGE_BASE_URL}${selectedFeed.publisher_profile_pic}`}
//                               className="w-16 h-16 rounded-full border-4 border-[#12082F] object-cover shadow-lg"
//                               alt="avatar"
//                             />
//                           )}
//                         </div>
//                         <h3 className="text-lg font-bold mb-1">
//                           {selectedFeed.meta?.users?.length > 1 
//                             ? selectedFeed.meta.users.map(e => e.first_name).join(' & ')
//                             : `${selectedFeed.meta?.first_name || ''} ${selectedFeed.meta?.last_name || ''}`
//                           }
//                         </h3>
//                         <p className="text-indigo-200 text-[10px] bg-indigo-500/15 px-3 py-1 rounded-full border border-indigo-500/30 mb-3 font-semibold">
//                           Completed {selectedFeed.meta?.users?.[0]?.years_completed || selectedFeed.meta?.years_completed || 1} Year Milestone
//                         </p>
//                         <p className="text-white/60 text-xs italic max-w-sm">
//                           "{selectedFeed.meta?.message || "Today marks a special milestone in your journey with us. Your dedication and passion have been truly inspiring!"}"
//                         </p>
//                       </div>
//                     ) : selectedFeed.type === "new_joiner" ? (
//                       /* NEW JOINER VIEW */
//                       <div className="w-full rounded-2xl overflow-hidden relative border border-white/5 p-6 bg-gradient-to-br from-[#1e1a5a] via-[#100c2f] to-[#0a081f] text-center flex flex-col items-center">
//                         <span className="text-[9px] font-black uppercase text-cyan-400 tracking-[6px] mb-1">Welcome to the Team</span>
//                         <h2 className="text-2xl font-black text-white leading-tight mb-4">NEW TEAM MEMBER</h2>
//                         <h3 className="text-lg font-bold">{selectedFeed.meta?.first_name} {selectedFeed.meta?.last_name}</h3>
//                         <p className="text-white/70 italic text-xs mt-0.5">{selectedFeed.meta?.job_title || "Innovative Team Member"}</p>
//                       </div>
//                     ) : selectedFeed.type === "employee_of_the_month" ? (
//                       /* STAR PERFORMER */
//                       <div className="w-full rounded-2xl overflow-hidden relative border border-yellow-500/20 p-6 bg-gradient-to-br from-[#1B103F] via-[#12082F] to-[#09061f] text-center flex flex-col items-center">
//                         <div className="mb-3 px-3 py-1 bg-yellow-500/10 rounded-full border border-yellow-500/30 text-yellow-400 text-[8px] uppercase tracking-widest font-black flex items-center gap-1">
//                           <Trophy size={10} />
//                           Employee of the Month
//                         </div>
//                         <h2 className="text-2xl font-black text-white leading-tight mb-5">STAR PERFORMER</h2>
//                         <img
//                           src={`${IMAGE_BASE_URL}${selectedFeed.meta?.winnerPic || '/default-avatar.png'}`}
//                           className="w-20 h-20 rounded-full border-4 border-[#12082F] object-cover shadow-xl mb-3"
//                           alt="star"
//                         />
//                         <h3 className="text-lg font-bold text-white mb-1.5">{selectedFeed.meta?.winnerName}</h3>
//                         <p className="text-yellow-200 text-[10px] font-bold bg-yellow-500/10 px-3 py-1 rounded-full border border-yellow-500/20">
//                           {selectedFeed.meta?.monthName} {selectedFeed.meta?.year}
//                         </p>
//                       </div>
//                     ) : (
//                       /* NORMAL POST LAYOUT */
//                       <div className="space-y-4">
//                         <h2 className="text-base font-black text-white/95 leading-snug">
//                           {selectedFeed.meta?.message?.split("\n")[0] || "Uptask Announcement"}
//                         </h2>
                        
//                         {/* Author Info Row */}
//                         <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white/5 border border-white/5 p-3 rounded-2xl shadow-inner">
//                           <div className="flex items-center gap-3">
//                             <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-purple-500 via-indigo-500 to-cyan-500 border-2 border-white/10 flex items-center justify-center font-black text-white text-xs shadow-md">
//                               {selectedFeed.publisher_name ? selectedFeed.publisher_name[0].toUpperCase() : "U"}
//                             </div>
//                             <div>
//                               <p className="text-xs font-black text-white/90 leading-none">{selectedFeed.publisher_name}</p>
//                               <div className="flex items-center gap-1.5 mt-1 text-[9px] text-white/40 font-medium">
//                                 <span>{moment(selectedFeed.created_at).format("DD MMM YYYY, hh:mm A")}</span>
//                                 <span>•</span>
//                                 <span className="flex items-center gap-0.5 text-cyan-400 font-semibold">
//                                   <Eye size={10} /> {selectedFeed.views} Views
//                                 </span>
//                               </div>
//                             </div>
//                           </div>
                          
//                           <div className="flex items-center gap-1.5 self-end sm:self-auto">
//                             <button
//                               onClick={() => handleShare(selectedFeed)}
//                               className="p-2 bg-white/5 hover:bg-white/10 border border-white/5 rounded-xl text-white/50 hover:text-white transition"
//                               title="Share post"
//                             >
//                               <Share2 size={13} />
//                             </button>
//                             {(isHR || selectedFeed.publisher_name?.includes(user?.first_name)) && (
//                               <button
//                                 onClick={() => handleDelete(selectedFeed.id)}
//                                 className="p-2 bg-red-500/5 hover:bg-red-500/10 border border-red-500/10 rounded-xl text-red-400/60 hover:text-red-400 transition"
//                                 title="Delete post"
//                               >
//                                 <Trash2 size={13} />
//                               </button>
//                             )}
//                           </div>
//                         </div>

//                         {/* Render Special Welcome Platform modules if Welcome Post */}
//                         {selectedFeed.isWelcome ? (
//                           <>
//                             {getWelcomeBanner()}
                            
//                             <div className="space-y-2 mt-4 bg-white/5 p-4 rounded-2xl border border-white/5">
//                               <p className="text-xs font-bold text-indigo-300">What you can do with Uptask:</p>
//                               <ul className="space-y-2">
//                                 {[
//                                   "Effortlessly manage projects and tasks",
//                                   "Track attendance and manage leaves",
//                                   "Automate payroll and HR processes",
//                                   "And much more..."
//                                 ].map((bullet, idx) => (
//                                   <li key={idx} className="flex items-center gap-2 text-xs text-white/80">
//                                     <CheckCircle2 size={14} className="text-purple-400 shrink-0 shadow-[0_0_8px_rgba(168,85,247,0.4)]" />
//                                     <span>{bullet}</span>
//                                   </li>
//                                 ))}
//                               </ul>
//                             </div>
//                           </>
//                         ) : (
//                           <>
//                             {/* Normal Post Image (Constrained to max-h-[300px] to prevent long scrolling) */}
//                             {selectedFeed.image && (
//                               <div className="rounded-2xl overflow-hidden bg-[#100825]/90 border border-white/5 max-h-[300px] flex items-center justify-center">
//                                 <img
//                                   src={`${IMAGE_BASE_URL}/${selectedFeed.image}`}
//                                   className="w-full max-h-[300px] object-contain mx-auto"
//                                   alt="post"
//                                 />
//                               </div>
//                             )}

//                             {/* Post description text */}
//                             <p className="text-xs text-white/70 leading-relaxed whitespace-pre-line font-light p-1">
//                               {selectedFeed.meta?.message}
//                             </p>
//                           </>
//                         )}
//                       </div>
//                     )}

//                     {/* ACTIONS ROW */}
//                     <div className="flex items-center gap-4 pt-3 border-t border-white/5 text-[10px] text-white/40 font-black uppercase tracking-wider">
//                       <button
//                         onClick={() => handleLike(selectedFeed)}
//                         className={`flex items-center gap-1.5 transition ${
//                           selectedFeed.is_liked ? "text-rose-500 scale-105" : "hover:text-rose-400"
//                         }`}
//                       >
//                         <Heart size={14} fill={selectedFeed.is_liked ? "currentColor" : "none"} />
//                         <span>{selectedFeed.total_likes || 0} Likes</span>
//                       </button>
//                       <div className="flex items-center gap-1.5">
//                         <MessageCircle size={14} />
//                         <span>{selectedFeed.comments?.length || 0} Comments</span>
//                       </div>
//                     </div>

//                     {/* COMMENTS LIST */}
//                     {selectedFeed.comments?.length > 0 && (
//                       <div className="space-y-2.5 pt-3 border-t border-white/5 max-h-[160px] overflow-y-auto no-scrollbar">
//                         {selectedFeed.comments.map((cmt) => (
//                           <div key={cmt.id} className="flex gap-2">
//                             <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-purple-500 to-indigo-500 flex items-center justify-center font-black text-[9px] text-white border border-white/10 shrink-0 shadow-sm">
//                               {cmt.user?.first_name ? cmt.user.first_name[0].toUpperCase() : "U"}
//                             </div>
//                             <div className="flex-1 bg-white/5 border border-white/5 rounded-2xl px-3.5 py-2 shadow-inner">
//                               <p className="text-[9px] font-black uppercase tracking-wider text-purple-300">
//                                 {cmt.user?.first_name} {cmt.user?.last_name}
//                               </p>
//                               <p className="text-[11px] text-white/80 mt-0.5 leading-snug">{cmt.comment}</p>
//                             </div>
//                           </div>
//                         ))}
//                       </div>
//                     )}
//                   </div>
//                 </div>

//                 {/* ADD COMMENT PANEL */}
//                 <form onSubmit={handleCommentSubmit} className="flex gap-2 border-t border-white/5 pt-3">
//                   <input
//                     type="text"
//                     value={commentText}
//                     onChange={(e) => setCommentText(e.target.value)}
//                     placeholder="Write a comment..."
//                     className="flex-1 bg-white/5 border border-white/10 px-4 py-2.5 rounded-xl text-xs text-white placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-purple-500/50"
//                   />
//                   <button
//                     type="submit"
//                     disabled={!commentText.trim()}
//                     className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:opacity-40 px-4.5 py-2 rounded-xl text-white transition active:scale-95 flex items-center justify-center font-bold shadow-md"
//                   >
//                     <Send size={13} />
//                   </button>
//                 </form>
//               </div>
//             ) : (
//               <div className="flex flex-col items-center justify-center h-full text-white/20 py-20">
//                 <FileText size={36} className="opacity-20 mb-3" />
//                 <h4 className="text-xs font-bold">No Announcement Selected</h4>
//               </div>
//             )}
//           </div>

//         </div>

//       </div>

//       {/* NEW POST CREATION MODAL */}
//       <CreatePostModal
//         showModal={showPostModal}
//         setShowModal={setShowPostModal}
//         onPostCreated={loadFeeds}
//       />
//     </div>
//   );
// }
